/**
 * <s-scroll-focus> — Scroll-linked blur-to-sharp reveal that works in every browser.
 *
 * Writes a 0–1 progress value to the `--focus` custom property on itself as it scrolls
 * through the viewport: 0 when its top edge reaches the bottom of the screen, 1 once its
 * top edge has travelled `data-distance` (default 0.6) of the viewport height. CSS in the
 * owning template maps `--focus` to blur/opacity/scale. Without JS, or with reduced motion,
 * `--focus` stays at 1 (fully sharp).
 */
class SScrollFocus extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.setProperty('--focus', '1');
      return;
    }

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#update();
      });
    };

    window.addEventListener('scroll', schedule, opts);
    window.addEventListener('resize', schedule, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #update() {
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    if (!viewport) return;

    const distance = Number.parseFloat(this.dataset.distance || '0.6') || 0.6;
    const top = this.getBoundingClientRect().top;
    const raw = (viewport - top) / (viewport * distance);
    const progress = Math.min(1, Math.max(0, raw));

    this.style.setProperty('--focus', progress.toFixed(3));
  }
}

if (!customElements.get('s-scroll-focus')) {
  customElements.define('s-scroll-focus', SScrollFocus);
}
